/**
 * Accessibility Utilities
 *
 * Provides functions and constants for improving accessibility (a11y).
 */

/**
 * Focus management utilities
 */
export const focusManager = {
  /**
   * Trap focus within a modal or dialog.
   * Re-evaluates focusable elements on content changes via MutationObserver.
   */
  trapFocus(container: HTMLElement, previousFocus: Element | null) {
    let firstFocusable: HTMLElement | undefined;
    let lastFocusable: HTMLElement | undefined;

    const updateFocusableElements = () => {
      const focusableElements = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable = focusableElements[0] as HTMLElement | undefined;
      lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement | undefined;
    };

    updateFocusableElements();

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (!firstFocusable || !lastFocusable) return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);

    const observer = new MutationObserver(() => {
      updateFocusableElements();
    });
    observer.observe(container, { childList: true, subtree: true });

    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleTabKey);
      observer.disconnect();
      (previousFocus as HTMLElement)?.focus();
    };
  },

  /**
   * Announce message to screen readers
   */
  announce(message: string, priority: 'polite' | 'assertive' = 'polite') {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      if (announcement.parentNode) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  },
};

/**
 * Keyboard shortcut utilities
 */
export const keyboardShortcuts = {
  /**
   * Check if keyboard shortcut matches
   */
  matches(event: KeyboardEvent, key: string, ctrl = false, shift = false, alt = false): boolean {
    return (
      event.key.toLowerCase() === key.toLowerCase() &&
      event.ctrlKey === ctrl &&
      event.shiftKey === shift &&
      event.altKey === alt
    );
  },

  /**
   * Format shortcut for display (e.g., "Ctrl+S")
   */
  formatShortcut(key: string, ctrl = false, shift = false, alt = false): string {
    const parts: string[] = [];
    if (ctrl) parts.push('Ctrl');
    if (alt) parts.push('Alt');
    if (shift) parts.push('Shift');
    parts.push(key);
    return parts.join('+');
  },
};

/**
 * ARIA label builders
 */
export const ariaLabels = {
  /**
   * Build label for action button
   */
  actionButton(action: string, itemName?: string): string {
    return itemName ? `${action} ${itemName}` : action;
  },

  /**
   * Build label for navigation link
   */
  navLink(pageName: string, isCurrent: boolean): string {
    return isCurrent ? `${pageName}, current page` : pageName;
  },

  /**
   * Build label for form input
   */
  formInput(label: string, required = false, error?: string): string {
    let text = label;
    if (required) text += ' (required)';
    if (error) text += `. Error: ${error}`;
    return text;
  },
};

/**
 * Color contrast utilities
 */
export const colorContrast = {
  /**
   * Calculate relative luminance of a color
   */
  getLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map((c) => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Calculate contrast ratio between two colors
   */
  getContrastRatio(lum1: number, lum2: number): number {
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
  },
};

/**
 * Skip link component helper
 */
export function createSkipLink(targetId: string, linkText = 'Skip to main content'): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = `#${targetId}`;
  link.textContent = linkText;
  link.className = 'skip-link';
  link.style.cssText = `
    position: absolute;
    top: -40px;
    left: 0;
    background: #f97316;
    color: white;
    padding: 8px 16px;
    text-decoration: none;
    z-index: 9999;
    transition: top 0.3s;
  `;

  link.addEventListener('focus', () => {
    link.style.top = '0';
  });

  link.addEventListener('blur', () => {
    link.style.top = '-40px';
  });

  link.addEventListener('click', (e) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.tabIndex = -1;
      target.focus();
      target.removeAttribute('tabindex');
    }
  });

  return link;
}

/**
 * Common ARIA attributes for components
 */
export const ariaCommon = {
  button: {
    role: 'button' as const,
  },
  navigation: {
    role: 'navigation' as const,
  },
  main: {
    role: 'main' as const,
  },
  complementary: {
    role: 'complementary' as const,
  },
  search: {
    role: 'search' as const,
  },
  alert: {
    role: 'alert' as const,
    'aria-live': 'assertive' as const,
  },
  status: {
    role: 'status' as const,
    'aria-live': 'polite' as const,
  },
};
